/**
 * Sample "Professional Bio" templates for the teacher profile.
 *
 * Each template is fill-in-the-blank: anything in [square brackets] is a placeholder the teacher
 * must replace. The profile form refuses to save a bio while any [bracketed] text remains — see
 * `hasBioPlaceholders` below.
 */

export interface BioTemplate {
  name: string;
  /** One-line summary shown under the name in the picker. */
  description: string;
  /** Plain-text paragraphs; rendered as one <p> each. */
  paragraphs: string[];
}

export const BIO_TEMPLATES: BioTemplate[] = [
  {
    name: 'School subject tutor',
    description: 'For tutors teaching school subjects and boards',
    paragraphs: [
      "Hi, I'm [Your Name], a [Subject] tutor with [X] years of experience teaching students in [Grades, e.g. 8–12] following the [CBSE / ICSE / IB / State Board] curriculum.",
      'I hold a [Degree] in [Field] from [University]. Over the years I have helped [number]+ students improve their grades, build strong fundamentals and gain confidence in [Subject].',
      'My classes are interactive and structured. I start by understanding where each student stands, then use clear explanations, worked examples and regular practice to close the gaps. I share notes and practice sheets after every session and keep parents updated on progress.',
      'Outside teaching, I enjoy [a hobby or interest]. I look forward to helping you reach your goals!',
    ],
  },
  {
    name: 'Competitive exam coach',
    description: 'For JEE, NEET, CAT, IELTS and other exam prep',
    paragraphs: [
      "I'm [Your Name], and I coach students for [Exam, e.g. JEE / NEET / CAT / IELTS]. I have [X] years of experience and have mentored [number]+ aspirants, including [a notable result, e.g. 15 students scoring 95+ percentile].",
      'I studied [Degree] at [University] and [cleared / scored X in] [Exam] myself, so I understand the pressure and the preparation it takes.',
      'My approach focuses on concept clarity first, then speed and accuracy. Each student gets a study plan, topic-wise tests, full-length mock exams with detailed analysis, and regular doubt-clearing sessions. I track weak areas closely and adjust the plan week by week.',
      'If you are serious about [Exam], I would love to help you prepare smarter.',
    ],
  },
  {
    name: 'Skill, language or music coach',
    description: 'For coding, languages, music, art and other skills',
    paragraphs: [
      "Hello! I'm [Your Name], a [Skill, e.g. guitar / spoken English / Python] coach with [X] years of teaching experience. I have taught [number]+ learners of all ages, from complete beginners to [advanced level].",
      '[One line about your background, e.g. I am a Trinity Grade 8 certified guitarist / a certified IELTS trainer / a software engineer at a product company.]',
      'I believe learning should be practical and enjoyable. Every lesson is tailored to your goals and pace, with hands-on practice, real examples and small milestones so you can see your progress. I give clear feedback and simple exercises to practise between sessions.',
      'Whether you are starting out or looking to level up, I will help you learn with confidence.',
    ],
  },
];

/** What the profile already knows about the teacher, used to pre-fill template placeholders. */
export interface BioProfileFacts {
  name?: string;
  /** Display name of the teacher's first subject. */
  subject?: string;
  /** Curriculum display labels, e.g. ["CBSE", "ICSE"]. */
  curricula?: string[];
  /** Highest qualification code, e.g. "MASTER". */
  qualification?: string;
  /** Free-text qualification when `qualification` is "OTHER". */
  otherQualification?: string;
  institution?: string;
  /** Teaching experience band code, e.g. "FIVE_TO_10". */
  experience?: string;
}

// Worded to follow "I hold a ..." in the templates. School-level qualifications are left
// out: "I hold a Secondary in [Field]" doesn't read, so [Degree] stays for the teacher.
const DEGREE_PHRASES: Record<string, string> = {
  DIPLOMA: 'Diploma',
  BACHELOR: "Bachelor's degree",
  MASTER: "Master's degree",
  DOCTORATE: 'PhD',
};

// Replaces "[X] years" as a unit so the band reads naturally ("less than a year of experience").
const EXPERIENCE_PHRASES: Record<string, string> = {
  LESS_THAN_1: 'less than a year',
  ONE_TO_3: '1–3 years',
  THREE_TO_5: '3–5 years',
  FIVE_TO_10: '5–10 years',
  MORE_THAN_10: '10+ years',
};

/**
 * Replaces the template placeholders the profile can already answer (name, subject, curricula,
 * credentials). Anything unknown keeps its [brackets], so `hasBioPlaceholders` still blocks
 * saving until the teacher fills it in.
 */
export function fillBioPlaceholders(
  text: string,
  facts: BioProfileFacts,
  escape: (value: string) => string = (v) => v,
): string {
  const clean = (v?: string) => (v ?? '').trim();
  const degree = facts.qualification === 'OTHER'
    ? clean(facts.otherQualification)
    : DEGREE_PHRASES[facts.qualification ?? ''] ?? '';
  const curricula = (facts.curricula ?? []).map(clean).filter(Boolean).join(' / ');

  const fills: Array<[RegExp, string]> = [
    [/\[Your Name\]/g, clean(facts.name)],
    [/\[Subject\]/g, clean(facts.subject)],
    [/\[Skill, e\.g\.[^\]]*\]/g, clean(facts.subject)],
    [/\[CBSE \/ ICSE \/ IB \/ State Board\]/g, curricula],
    [/\[X\] years/g, EXPERIENCE_PHRASES[facts.experience ?? ''] ?? ''],
    [/\[Degree\]/g, degree],
    [/\[University\]/g, clean(facts.institution)],
  ];
  return fills.reduce(
    (out, [re, value]) => (value ? out.replace(re, () => escape(value)) : out),
    text,
  );
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The template as editor HTML: one <p> per paragraph, with known placeholders pre-filled. */
export function bioTemplateToHtml(template: BioTemplate, facts: BioProfileFacts = {}): string {
  return template.paragraphs
    .map((p) => `<p>${fillBioPlaceholders(p, facts, escapeHtml)}</p>`)
    .join('');
}

export const BIO_PLACEHOLDER_ERROR = 'Replace the [bracketed] text in your bio with your own details.';

const PLACEHOLDER_RE = /\[[^[\]]+\]/g;

// Only the templates' own placeholders count, so a teacher's genuine bracketed
// text (e.g. "Maths [CBSE]") never blocks saving.
const TEMPLATE_PLACEHOLDERS = new Set(
  BIO_TEMPLATES.flatMap(t => t.paragraphs.join(' ').match(PLACEHOLDER_RE) ?? []),
);

/** Plain text of editor HTML: tags removed, common entities decoded. */
export function stripBioHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

/** True while the bio (HTML or plain text) still contains a [bracketed] template placeholder. */
export function hasBioPlaceholders(text: string): boolean {
  return (stripBioHtml(text).match(PLACEHOLDER_RE) ?? []).some(p => TEMPLATE_PLACEHOLDERS.has(p));
}
